import { useEffect, useState, type FormEvent } from "react";
import { PageHeader } from "../components/PageHeader";
import { siteConfig } from "../config/site";

/**
 * The form posts to the contact Worker, which holds the Cloudflare email
 * binding. In production the Worker runs on a route of this same zone, so the
 * default relative path is same-origin; `npm run dev` proxies it to a local
 * `wrangler dev` (see vite.config.ts).
 */
const endpoint = import.meta.env.VITE_CONTACT_ENDPOINT ?? "/api/contact";

const MESSAGE_MAX = 2000;
const MESSAGE_MIN = 20;

const fieldClass =
  "w-full border border-zinc-800 bg-[#090909] px-3.5 py-3 font-mono text-[11px] tracking-[0.08em] text-zinc-100 outline-none transition-colors placeholder:text-zinc-700 focus:border-zinc-100 disabled:opacity-50";

const labelClass =
  "font-mono text-[8px] tracking-[0.2em] text-zinc-600";

/**
 * The Worker enforces the real cooldown; this only mirrors it so the button
 * reflects the wait instead of firing requests that are already going to be
 * refused. It survives a reload, which a bare piece of state would not.
 */
const COOLDOWN_KEY = "pd:contact:cooldown";

function readCooldown(): number {
  try {
    return Number(window.localStorage.getItem(COOLDOWN_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeCooldown(until: number) {
  try {
    window.localStorage.setItem(COOLDOWN_KEY, String(until));
  } catch {
    // Private-mode storage failures are not worth blocking a submission over.
  }
}

interface FormState {
  name: string;
  company: string;
  email: string;
  message: string;
}

const emptyForm: FormState = { name: "", company: "", email: "", message: "" };

export function ContactPage() {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [topic, setTopic] = useState<string>(siteConfig.contact.topics[0]);
  const [honeypot, setHoneypot] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [reference, setReference] = useState<string | null>(null);
  const [cooldownUntil, setCooldownUntil] = useState<number>(readCooldown);
  const [now, setNow] = useState<number>(() => Date.now());

  const waiting = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));

  useEffect(() => {
    if (cooldownUntil <= Date.now()) return;

    const timer = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(timer);
  }, [cooldownUntil]);

  function startCooldown(seconds: number) {
    const until = Date.now() + seconds * 1000;

    setCooldownUntil(until);
    setNow(Date.now());
    writeCooldown(until);
  }

  const update = (key: keyof FormState) => (value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError(null);
  };

  /** Mirrors the Worker's validation so the common mistakes never leave the page. */
  function localError(): string | null {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      return "NAME, EMAIL ADDRESS AND MESSAGE ARE REQUIRED.";
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      return "THE EMAIL ADDRESS ENTERED IS NOT VALID.";
    }
    if (form.message.trim().length < MESSAGE_MIN) {
      return "PLEASE PROVIDE FURTHER DETAIL BEFORE SUBMITTING.";
    }
    return null;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (pending) return;

    if (waiting > 0) {
      setError(`PLEASE WAIT ${waiting} SECONDS BEFORE SENDING ANOTHER ENQUIRY.`);
      return;
    }

    const invalid = localError();

    if (invalid) {
      setError(invalid);
      return;
    }

    setPending(true);
    setError(null);

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          topic,
          name: form.name.trim(),
          company: form.company.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
          website: honeypot,
        }),
      });

      const payload = (await response.json().catch(() => null)) as {
        reference?: string;
        error?: string;
        retryAfter?: number;
      } | null;

      if (typeof payload?.retryAfter === "number") {
        startCooldown(payload.retryAfter);
      }

      if (!response.ok || !payload?.reference) {
        setError(
          payload?.error ??
            "THE ENQUIRY COULD NOT BE SENT. PLEASE TRY AGAIN SHORTLY.",
        );
        return;
      }

      setReference(payload.reference);
    } catch {
      setError("THE ENQUIRY COULD NOT BE SENT. PLEASE CHECK YOUR CONNECTION.");
    } finally {
      setPending(false);
    }
  }

  function reset() {
    setForm(emptyForm);
    setTopic(siteConfig.contact.topics[0]);
    setHoneypot("");
    setReference(null);
    setError(null);
  }

  return (
    <section className="mx-auto w-full max-w-6xl px-5 py-6 sm:p-10 lg:p-12">
      <PageHeader
        eyebrow="CONTACT // ENQUIRIES"
        title={siteConfig.contact.title}
        description={siteConfig.contact.description}
      />

      <div className="mt-8 grid gap-px border border-zinc-800 bg-zinc-800 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="bg-[#0b0b0b] p-6 sm:p-8">
          {reference ? (
            <div className="grid min-h-80 place-items-center text-center">
              <div>
                <p className="font-mono text-[9px] tracking-[0.24em] text-zinc-600">
                  ENQUIRY RECEIVED
                </p>
                <p className="mt-5 font-mono text-[22px] tracking-[0.1em] text-zinc-100">
                  {reference}
                </p>
                <p className="mx-auto mt-5 max-w-96 text-sm font-light leading-[1.9rem] text-zinc-400">
                  Please retain this reference for any follow-up
                  correspondence. Our reply will be sent to the address you
                  provided; if you have not heard from us within one week,
                  kindly check your spam folder.
                </p>
                <button
                  type="button"
                  onClick={reset}
                  disabled={waiting > 0}
                  className="mt-7 inline-block border border-zinc-700 px-4 py-3 font-mono text-[9px] tracking-[0.16em] text-zinc-400 transition-colors hover:border-zinc-100 hover:bg-zinc-100 hover:text-black focus-visible:border-zinc-100 focus-visible:bg-zinc-100 focus-visible:text-black focus-visible:outline-none disabled:cursor-not-allowed disabled:border-zinc-800 disabled:bg-transparent disabled:text-zinc-700"
                >
                  {waiting > 0
                    ? `SEND ANOTHER IN ${waiting}S`
                    : "SEND ANOTHER ENQUIRY"}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <fieldset className="m-0 border-0 p-0" disabled={pending}>
                <legend className="mb-2.5 font-mono text-[9px] tracking-[0.22em] text-zinc-600">
                  SUBJECT
                </legend>

                <div className="flex flex-wrap gap-2">
                  {siteConfig.contact.topics.map((entry) => {
                    const active = entry === topic;

                    return (
                      <button
                        key={entry}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setTopic(entry)}
                        className={[
                          "border px-3.5 py-[11px] font-mono text-[9px] tracking-[0.14em] transition-all focus-visible:outline-none",
                          active
                            ? "border-zinc-100 bg-zinc-100 text-black"
                            : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-100 focus-visible:border-zinc-100",
                        ].join(" ")}
                      >
                        {entry}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <label className="flex flex-col gap-2">
                    <span className={labelClass}>FULL NAME</span>
                    <input
                      type="text"
                      name="name"
                      autoComplete="name"
                      maxLength={120}
                      value={form.name}
                      onChange={(event) => update("name")(event.target.value)}
                      placeholder="Your full name"
                      className={fieldClass}
                    />
                  </label>

                  <label className="flex flex-col gap-2">
                    <span className={labelClass}>COMPANY / STUDIO</span>
                    <input
                      type="text"
                      name="organization"
                      autoComplete="organization"
                      maxLength={120}
                      value={form.company}
                      onChange={(event) =>
                        update("company")(event.target.value)
                      }
                      placeholder="Optional"
                      className={fieldClass}
                    />
                  </label>

                  <label className="flex flex-col gap-2 sm:col-span-2">
                    <span className={labelClass}>EMAIL ADDRESS</span>
                    <input
                      type="email"
                      name="email"
                      autoComplete="email"
                      maxLength={200}
                      value={form.email}
                      onChange={(event) => update("email")(event.target.value)}
                      placeholder="Address for our reply"
                      className={fieldClass}
                    />
                  </label>

                  <label className="flex flex-col gap-2 sm:col-span-2">
                    <span className={labelClass}>MESSAGE</span>
                    <textarea
                      rows={7}
                      name="message"
                      maxLength={MESSAGE_MAX}
                      value={form.message}
                      onChange={(event) =>
                        update("message")(event.target.value)
                      }
                      placeholder="Please describe your enquiry in as much detail as you can provide."
                      className={`${fieldClass} resize-y p-3.5 leading-[1.9] tracking-[0.06em]`}
                    />
                  </label>
                </div>

                {/* Not shown to people; anything that fills it in is a bot. */}
                <label className="hidden" aria-hidden="true">
                  Leave this field empty
                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(event) => setHoneypot(event.target.value)}
                  />
                </label>

                <div className="mt-3 flex items-center justify-between gap-4">
                  <span className="font-mono text-[8px] tracking-[0.16em] text-zinc-700">
                    {String(form.message.length).padStart(4, "0")} /{" "}
                    {MESSAGE_MAX} CHARACTERS
                  </span>
                </div>

                {error ? (
                  <p
                    role="alert"
                    className="mt-4 border border-zinc-600 px-3.5 py-3 font-mono text-[8px] leading-[1.7] tracking-[0.14em] text-zinc-300"
                  >
                    {error}
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={waiting > 0}
                  className="mt-5 flex w-full items-center justify-between gap-4 border border-zinc-100 bg-zinc-100 px-5 py-4 font-mono text-[10px] font-semibold tracking-[0.18em] text-black transition-colors hover:bg-transparent hover:text-zinc-100 focus-visible:bg-transparent focus-visible:text-zinc-100 focus-visible:outline-none disabled:cursor-not-allowed disabled:border-zinc-800 disabled:bg-transparent disabled:text-zinc-600"
                >
                  {waiting > 0
                    ? `COOLDOWN — ${waiting}S`
                    : pending
                      ? "SENDING ENQUIRY"
                      : "SUBMIT ENQUIRY"}
                  <span aria-hidden="true">→</span>
                </button>
              </fieldset>
            </form>
          )}
        </div>

        <aside className="bg-[#090909] p-6 sm:p-8">
          <p className="font-mono text-[9px] tracking-[0.22em] text-zinc-600">
            RESPONSE
          </p>
          <dl className="mt-3.5 border border-zinc-800 bg-[#0a0a0a]">
            {siteConfig.contact.response.map((row) => (
              <div
                key={row.label}
                className="grid grid-cols-[6.5rem_minmax(0,1fr)] border-b border-zinc-800 last:border-b-0"
              >
                <dt className="border-r border-zinc-800 p-3 font-mono text-[8px] tracking-[0.18em] text-zinc-700">
                  {row.label}
                </dt>
                <dd className="m-0 p-3 font-mono text-[9px] tracking-[0.12em] text-zinc-400">
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>

          <p className="mt-7 font-mono text-[9px] tracking-[0.22em] text-zinc-600">
            BEFORE SUBMITTING
          </p>
          <ul className="mt-3.5 flex list-none flex-col gap-3 p-0">
            {siteConfig.contact.notes.map((note) => (
              <li
                key={note.n}
                className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2 text-[13px] font-light leading-[1.7rem] text-zinc-500"
              >
                <span
                  aria-hidden="true"
                  className="font-mono text-[8px] tracking-[0.16em] text-zinc-700"
                >
                  {note.n}
                </span>
                {note.text}
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </section>
  );
}
